import React from "react";
import {
  Stack,
  Dropdown,
  IDropdownOption,
  Text,
  IconButton,
} from "@fluentui/react";

export interface PaginationFooterProps {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  /** Optional — when provided, renders the "Results per page" dropdown. */
  onPageSizeChange?: (size: number) => void;
  /** Optional — when provided, renders centered "{N} selected" text. */
  selectedCount?: number;
  /** Page size options shown in the dropdown. Defaults to [10, 25, 50]. */
  pageSizeOptions?: number[];
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];

export const PaginationFooter: React.FC<PaginationFooterProps> = ({
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  selectedCount,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const dropdownOptions: IDropdownOption[] = pageSizeOptions.map((n) => ({
    key: n,
    text: String(n),
  }));

  const getPaginationNumbers = () => {
    const maxVisiblePages = 5;
    const pages: (number | string)[] = [];

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Show pages 1, 2, 3, 4, ..., last
        pages.push(2, 3, 4);
        if (totalPages > 4) {
          pages.push("...");
          pages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 2) {
        // Show pages 1, ..., last-3, last-2, last-1, last
        if (totalPages > 4) {
          pages.push("...");
        }
        for (let i = totalPages - 3; i <= totalPages; i++) {
          if (i > 1 && !pages.includes(i)) {
            pages.push(i);
          }
        }
      } else {
        // Show pages 1, ..., current-1, current, current+1, ..., last
        pages.push("...");
        pages.push(currentPage - 1, currentPage, currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Hide entirely when there's nothing to show and no controls to render.
  if (totalCount <= 0 && !onPageSizeChange && selectedCount === undefined) {
    return null;
  }

  const paginationNumbers = getPaginationNumbers();

  return (
    <Stack
      horizontal
      horizontalAlign="space-between"
      verticalAlign="center"
      tokens={{ childrenGap: 16 }}
      styles={{ root: { padding: "12px 0" } }}
    >
      {/* Results info */}
      <Stack.Item>
        <Text variant="medium" styles={{ root: { color: "#605e5c" } }}>
          Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)}-
          {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
        </Text>
      </Stack.Item>

      {/* Selected count (centered) — only when caller opts in */}
      {selectedCount !== undefined && (
        <Stack.Item
          grow
          styles={{ root: { display: "flex", justifyContent: "center" } }}
        >
          <Text
            variant="medium"
            styles={{ root: { color: "#605e5c", fontWeight: 600 } }}
          >
            {selectedCount} selected
          </Text>
        </Stack.Item>
      )}

      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 16 }}>
        {/* Results per page dropdown */}
        {onPageSizeChange && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="medium">Results per page</Text>
            <Dropdown
              options={dropdownOptions}
              selectedKey={pageSize}
              onChange={(_, option) => {
                if (option) {
                  onPageSizeChange(option.key as number);
                }
              }}
              styles={{
                dropdown: { width: 70 },
                title: { border: "1px solid #ccc", height: 32 },
              }}
            />
          </Stack>
        )}

        {/* Page navigation */}
        {totalPages > 1 && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            {/* Previous button */}
            <IconButton
              iconProps={{ iconName: "ChevronLeft" }}
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
              styles={{
                root: {
                  color: currentPage === 1 ? "#a19f9d" : "#323130",
                  backgroundColor: "transparent",
                  border: "1px solid #ccc",
                  minWidth: 32,
                  height: 32,
                },
                rootHovered: {
                  backgroundColor:
                    currentPage === 1 ? "transparent" : "#f3f2f1",
                },
                icon: { fontSize: 12 },
              }}
            />

            {/* Page numbers */}
            {paginationNumbers.map((page, index) => (
              <React.Fragment key={index}>
                {page === "..." ? (
                  <Text
                    variant="medium"
                    styles={{
                      root: {
                        padding: "6px 8px",
                        color: "#605e5c",
                        minWidth: 32,
                        textAlign: "center",
                      },
                    }}
                  >
                    ...
                  </Text>
                ) : (
                  <button
                    onClick={() => onPageChange(page as number)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ccc",
                      backgroundColor:
                        currentPage === page ? "#0078d4" : "#f3f2f1",
                      color: currentPage === page ? "white" : "#323130",
                      cursor: "pointer",
                      minWidth: 32,
                      height: 32,
                      borderRadius: 2,
                    }}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}

            {/* Next button */}
            <IconButton
              iconProps={{ iconName: "ChevronRight" }}
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              styles={{
                root: {
                  color: currentPage === totalPages ? "#a19f9d" : "#323130",
                  backgroundColor: "transparent",
                  border: "1px solid #ccc",
                  minWidth: 32,
                  height: 32,
                },
                rootHovered: {
                  backgroundColor:
                    currentPage === totalPages ? "transparent" : "#f3f2f1",
                },
                icon: { fontSize: 12 },
              }}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default PaginationFooter;
